"use client";

import { memo, useId, useRef } from "react";
import { Noise } from "@ui/components/core/Noise/Noise";
import type { OrbShaderProps } from "./OrbShader";
import type { OrbTexture, OrbTextureName } from "./textures";
import { TEXTURES } from "./textures";
import Image from "next/image";
import dynamic from "next/dynamic";
import { AnimatePresence, useInView } from "motion/react";
import * as m from "motion/react-m";

export type { OrbTexture, OrbTextureName } from "./textures";
export { ORB_TEXTURE_NAMES } from "./textures";

const OrbShader = dynamic(() => import("./OrbShader").then(mod => mod.OrbShader), { ssr: false });

export interface OrbProps extends Omit<OrbShaderProps, "texture"> {
  texture: OrbTextureName | OrbTexture;
  placeholder?: boolean;
  placeholderBlur?: number;
  image?: { sizes: string };
  forceShaderRendering?: boolean;
  noiseScale?: number;
  exitDuration?: number;
}

export const Orb = memo(function Orb({
  texture: textureProp,
  placeholder = true,
  placeholderBlur,
  image,
  animated = true,
  fadeInDuration = image ? 0 : 1,
  forceShaderRendering = false,
  noiseScale,
  width,
  height,
  exitDuration = 0.3,
  ...orbShaderProps
}: OrbProps) {
  const texture = typeof textureProp === "string" ? TEXTURES[textureProp] : textureProp;
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref);
  const noiseOpacity = texture.noiseOpacity ?? 0.5;

  return (
    <div ref={ref} className='tw-absolute tw-inset-0'>
      {placeholder && texture.placeholder && (
        <OrbPlaceholder
          src={texture.placeholder}
          blur={placeholderBlur}
          className='tw-absolute tw-inset-0 tw-size-full'
        />
      )}
      {image && <Image src={texture.image} alt='' className='tw-object-cover' sizes={image.sizes} fill />}
      <AnimatePresence initial={false}>
        {((animated && inView) || forceShaderRendering) && (
          <m.div
            className='tw-absolute tw-inset-0'
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: exitDuration } }}
          >
            <OrbShader
              texture={texture.texture}
              saturation={texture.saturation}
              fadeInDuration={forceShaderRendering ? 0 : fadeInDuration}
              animated={forceShaderRendering ? animated : undefined}
              width={width}
              height={height}
              {...orbShaderProps}
            />
          </m.div>
        )}
      </AnimatePresence>
      {noiseOpacity > 0 && (
        <div className='tw-absolute tw-inset-0 tw-mix-blend-overlay'>
          <Noise style={{ opacity: noiseOpacity }} scale={noiseScale} />
        </div>
      )}
    </div>
  );
});

function OrbPlaceholder({
  src,
  blur = 8,
  ...props
}: React.ComponentPropsWithoutRef<"svg"> & { src: string; blur?: number }) {
  const id = useId();

  return (
    <svg aria-hidden {...props}>
      <filter id={id} colorInterpolationFilters='sRGB'>
        <feGaussianBlur stdDeviation={blur} />
        <feColorMatrix values='1 0 0 0 0 0 1 0 0 0 0 0 1 0 0 0 0 0 100 -1' result='s' />
        <feFlood x='0' y='0' width='100%' height='100%' />
        <feComposite operator='out' in='s' />
        <feComposite in2='SourceGraphic' />
        <feGaussianBlur stdDeviation={blur} />
      </filter>
      <image
        width='100%'
        height='100%'
        preserveAspectRatio='xMidYMid slice'
        style={{ filter: `url(#${id})` }}
        href={src}
      />
    </svg>
  );
}
